import { useState } from 'react'
import { Check, Copy, ExternalLink, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { useRoles } from '@/features/roles/hooks'
import { useSaveSSOSettings, useSSOSettings } from '../hooks'
import { GoogleIcon } from '../google-icon'

interface SSOFormState {
  client_id: string
  client_secret: string // "" = keep the stored secret
  allowed_domain: string
  auto_provision: boolean
  default_role_id: string // "" = none
  enabled: boolean
}

export function SSOTab() {
  const { data: settings, isLoading, isError } = useSSOSettings()
  const saveSettings = useSaveSSOSettings()
  // Bumped after each save so the form remounts from the fresh server state
  // (secret cleared, domain lowercased) instead of syncing via effects.
  const [savedAt, setSavedAt] = useState(0)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GoogleIcon className="h-4 w-4" /> Google sign-in
        </CardTitle>
        <CardDescription>
          Let people sign in with their Google account. Their Google email must match a user's
          email above, or — with auto-provisioning on — they get a user created with the default
          role on first sign-in.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-5">
        {isLoading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : isError || !settings ? (
          <p className="text-destructive text-sm">Failed to load Google sign-in settings.</p>
        ) : (
          <SSOForm
            key={savedAt}
            settings={settings}
            saved={savedAt > 0}
            saving={saveSettings.isPending}
            saveError={saveSettings.error instanceof Error ? saveSettings.error.message : null}
            onSave={(body) =>
              saveSettings.mutate(body, {
                onSuccess: () => setSavedAt(Date.now()),
              })
            }
          />
        )}
      </CardContent>
    </Card>
  )
}

interface SSOFormBody {
  client_id: string
  client_secret?: string
  allowed_domain: string
  auto_provision: boolean
  default_role_id: string
  enabled: boolean
}

function SSOForm({
  settings,
  saved,
  saving,
  saveError,
  onSave,
}: {
  settings: {
    client_id: string
    allowed_domain: string
    auto_provision: boolean
    default_role_id: string
    enabled: boolean
    secret_set: boolean
  }
  saved: boolean
  saving: boolean
  saveError: string | null
  onSave: (body: SSOFormBody) => void
}) {
  const { data: roles } = useRoles()
  const [form, setForm] = useState<SSOFormState>({
    client_id: settings.client_id,
    client_secret: '',
    allowed_domain: settings.allowed_domain,
    auto_provision: settings.auto_provision,
    default_role_id: settings.default_role_id,
    enabled: settings.enabled,
  })
  const [copied, setCopied] = useState(false)

  const redirectURI = `${window.location.origin}/login`

  const onCopy = async () => {
    await navigator.clipboard.writeText(redirectURI)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <>
      <ol className="text-muted-foreground grid gap-2 text-sm">
        <li>
          <span className="text-foreground font-medium">1.</span> In the{' '}
          <a
            href="https://console.cloud.google.com/apis/credentials"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-0.5 underline underline-offset-4"
          >
            Google Cloud Console <ExternalLink className="h-3 w-3" />
          </a>
          , create an OAuth 2.0 Client ID for a Web application.
        </li>
        <li>
          <span className="text-foreground font-medium">2.</span> Add this authorized redirect URI:
          <span className="mt-1 flex items-center gap-2">
            <code className="bg-muted rounded px-2 py-1 text-xs">{redirectURI}</code>
            <Button variant="ghost" size="icon" onClick={onCopy} title="Copy redirect URI">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </span>
        </li>
        <li>
          <span className="text-foreground font-medium">3.</span> Paste the client ID and secret
          below and save.
        </li>
      </ol>

      <Separator />

      <div className="grid gap-3">
        <div className="grid gap-2">
          <Label htmlFor="sso-client-id">Client ID</Label>
          <Input
            id="sso-client-id"
            value={form.client_id}
            placeholder="1234567890-abc.apps.googleusercontent.com"
            autoCapitalize="none"
            onChange={(e) => setForm({ ...form, client_id: e.target.value })}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="sso-client-secret">Client secret</Label>
          <Input
            id="sso-client-secret"
            type="password"
            value={form.client_secret}
            placeholder={settings.secret_set ? 'stored — leave blank to keep' : 'required'}
            onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="sso-domain">Allowed Google domain (optional)</Label>
          <Input
            id="sso-domain"
            value={form.allowed_domain}
            placeholder="e.g. yourmarina.com — blank allows any Google account"
            autoCapitalize="none"
            onChange={(e) => setForm({ ...form, allowed_domain: e.target.value })}
          />
        </div>
        <div className="grid gap-3 rounded-md border p-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={form.auto_provision}
              onCheckedChange={(checked) =>
                setForm({ ...form, auto_provision: checked === true })
              }
            />
            Automatically create users the first time they sign in
          </label>
          <div className="grid gap-2">
            <Label htmlFor="sso-default-role">Role for auto-created users</Label>
            <Select
              value={form.default_role_id}
              disabled={!form.auto_provision}
              onValueChange={(v) => setForm({ ...form, default_role_id: v })}
            >
              <SelectTrigger id="sso-default-role">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {(roles ?? []).map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    {role.name}
                    {role.is_system ? ' (full access)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">
              {form.auto_provision
                ? 'Anyone who completes Google sign-in with an email matching no existing user gets a user created here with this role. Turn it off to require an admin to add people first.'
                : 'Unknown emails are rejected — an admin adds people on the Users tab first.'}
            </p>
          </div>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <Checkbox
            checked={form.enabled}
            onCheckedChange={(checked) => setForm({ ...form, enabled: checked === true })}
          />
          Enable "Sign in with Google" on the login page
        </label>
        {saveError && <p className="text-destructive text-sm">{saveError}</p>}
        <div className="flex items-center gap-3">
          <Button
            onClick={() =>
              onSave({
                client_id: form.client_id.trim(),
                client_secret: form.client_secret.trim() || undefined,
                allowed_domain: form.allowed_domain.trim(),
                auto_provision: form.auto_provision,
                default_role_id: form.auto_provision ? form.default_role_id : '',
                enabled: form.enabled,
              })
            }
            disabled={saving}
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
          {saved && <span className="text-muted-foreground text-sm">Saved.</span>}
        </div>
      </div>
    </>
  )
}
